/**
 * Social-preview (Open Graph / Twitter Card) metadata for the public
 * deal-landing page. Pure function, no JSX — `app/[slug].tsx` renders the
 * result into `<Head>`, and this is the only part of the OG path a unit
 * test can reach (CI runs typecheck/lint/jest and never exports web).
 *
 * Every string here is copy a human wrote, read straight from a deal
 * column — never assembled from one.
 *
 * The previous version interpolated `brand_name` and `user_cut_cents` into
 * both the title and the description fallback, which produced two live
 * defects:
 *
 *   - `sofi-checking-2026`'s headline claimed $20 (MarketPryce's cut) while
 *     its own description promised $50 (the total, $20 from us + $30 from
 *     SoFi), so the card contradicted itself. The same template made
 *     Checking and Crypto render byte-identical titles, since both pay $20.
 *
 *   - Any deal with `marketing_copy: {}` got "Open a new account at {brand}
 *     and get paid ${n} in 24 hours" — false for Capital One (a Safari
 *     extension install) and for Upside (gas cashback that settles after
 *     purchase verification, with no 24-hour payout).
 *
 * A template can only be as correct as its assumptions about every deal
 * shape, and `Deal#title` / `Deal#description` are both `presence: true` on
 * the model, so there is always human copy to read instead. Hence no
 * template — the only interpolation left is the fixed `OG_TITLE_SUFFIX`.
 *
 *     deal resolved ──► title: deal.title + suffix
 *                       desc:  hero_subhead ?? description ?? deal.title
 *
 *     no deal      ──► site default (unknown slug, API down). `url` is the
 *                      site root unless the caller knows the canonical —
 *                      the share-preview middleware does (it has the slug
 *                      from the request) and passes it, so a transient API
 *                      failure never rewrites a card's identity.
 *
 * Who calls this: `app/[slug].tsx` for the page a human sees, and
 * `lib/linkPreview.ts` for the HTML a crawler gets from the server
 * middleware. One function, so the two can't drift.
 */
import {
  APP_BASE_URL,
  DEFAULT_OG_DESCRIPTION,
  DEFAULT_OG_IMAGE,
  DEFAULT_OG_TITLE,
  OG_TITLE_SUFFIX,
} from '@/constants/og';
import type { PublicSignupDeal } from '@/services/publicSignup';

export type DealOg = {
  title: string;
  description: string;
  image: string;
  url: string;
};

export interface BuildOgOptions {
  /** Canonical to emit when there is no deal to derive one from. */
  url?: string;
}

export function buildOg(slug: string, deal: PublicSignupDeal | null, options: BuildOgOptions = {}): DealOg {
  if (deal) {
    return {
      title: `${deal.title}${OG_TITLE_SUFFIX}`,
      // `hero_subhead` is the deliberately short preview form; `description`
      // is the full approved copy.
      //
      // The last link is `title`, not DEFAULT_OG_DESCRIPTION: the site
      // description says "opening accounts" and "banking signups", which
      // would be a fresh false claim on a gas-cashback or browser-extension
      // deal — the exact failure this function exists to remove. Redundant
      // with og:title if it ever fires, which beats wrong.
      //
      // Per the serializer contract in `services/publicSignup.ts`, `title`
      // is required and `description` is nullable, which is why the chain
      // ends where it does. The optional chain on `marketing_copy` is for a
      // malformed response.
      description: deal.marketing_copy?.hero_subhead ?? deal.description ?? deal.title,
      // Always DEFAULT_OG_IMAGE — see the note on that constant for why a
      // brand logo would downgrade the card.
      image: DEFAULT_OG_IMAGE,
      url: `${APP_BASE_URL}/${deal.slug}`,
    };
  }

  // No deal: the site default. `url` falls back to the site root, not
  // `${APP_BASE_URL}/${slug}` — `slug` here can be the literal "[slug]" of
  // Expo's dynamic fallback page, and a deal we can't resolve has no
  // canonical we can vouch for. A caller that does know it passes it in.
  return {
    title: DEFAULT_OG_TITLE,
    description: DEFAULT_OG_DESCRIPTION,
    image: DEFAULT_OG_IMAGE,
    url: options.url ?? APP_BASE_URL,
  };
}
